import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import apiClient from '../services/apiClient';

// Define types
export interface User {
  _id: string;
  id?: string;
  username: string;
  displayName?: string;
  email: string;
  points: number;
  reputationScore: number;
  badges: string[];
  status: string;
  createdAt: string;
  role?: string;
  theme?: 'light' | 'dark';
}

interface AuthState {
  authToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  user: User | null;
  error: string | null;
}

type AuthAction =
  | { type: 'USER_LOADED'; payload: User }
  | { type: 'LOGIN_SUCCESS'; payload: { authToken: string } }
  | { type: 'REGISTER_SUCCESS'; payload: { authToken: string } }
  | { type: 'AUTH_ERROR'; payload?: string }
  | { type: 'LOGIN_FAIL'; payload: string }
  | { type: 'REGISTER_FAIL'; payload: string }
  | { type: 'PROFILE_UPDATE_SUCCESS'; payload: User }
  | { type: 'PROFILE_UPDATE_FAIL'; payload: string }
  | { type: 'PASSWORD_UPDATE_SUCCESS' }
  | { type: 'PASSWORD_UPDATE_FAIL'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERRORS' };

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  loginWithToken: (authToken: string) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: {
    username?: string;
    email?: string;
    displayName?: string;
  }) => Promise<void>;
  updatePassword: (passwords: { currentPassword: string; newPassword: string }) => Promise<void>;
  clearErrors: () => void;
}

// Initial state
const initialState: AuthState = {
  authToken: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  user: null,
  error: null,
};

// Create context
export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  login: async () => {},
  register: async () => {},
  loginWithToken: async () => {},
  logout: () => {},
  updateProfile: async () => {},
  updatePassword: async () => {},
  clearErrors: () => {},
});

// Set auth token in localStorage (apiClient handles headers automatically)
export const setAuthToken = (authToken: string | null) => {
  if (authToken) {
    localStorage.setItem('token', authToken);
  } else {
    localStorage.removeItem('token');
  }
};

// Reducer function
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'USER_LOADED':
    case 'PROFILE_UPDATE_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: {
          ...action.payload,
          // Map _id to id for compatibility with some components
          id: action.payload._id,
        },
      };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      setAuthToken(action.payload.authToken);
      return {
        ...state,
        authToken: action.payload.authToken,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case 'AUTH_ERROR':
    case 'LOGIN_FAIL':
    case 'REGISTER_FAIL':
    case 'LOGOUT':
      setAuthToken(null);
      return {
        ...state,
        authToken: null,
        isAuthenticated: false,
        loading: false,
        user: null,
        error: action.type === 'LOGOUT' ? null : action.payload || null,
      };
    case 'PROFILE_UPDATE_FAIL':
    case 'PASSWORD_UPDATE_FAIL':
      return {
        ...state,
        error: action.payload,
      };
    case 'PASSWORD_UPDATE_SUCCESS':
      return {
        ...state,
        error: null,
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set token in axios headers initially
  useEffect(() => {
    if (localStorage.token) {
      setAuthToken(localStorage.token);
    }
  }, []);

  // Load user if token exists
  useEffect(() => {
    const loadUser = async () => {
      const storedAuthToken = localStorage.getItem('token');

      if (storedAuthToken) {
        try {
          const res = await apiClient.get('/auth/me');
          dispatch({ type: 'USER_LOADED', payload: res.data.data });
        } catch (err) {
          console.error('Error loading user:', err);
          dispatch({ type: 'AUTH_ERROR', payload: 'Authentication failed' });
        }
      } else {
        dispatch({ type: 'AUTH_ERROR' });
      }
    };

    loadUser();
  }, []);

  // Memoize functions to prevent unnecessary re-renders
  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await apiClient.post('/auth/login', { email, password });

      // CRITICAL FIX: Set token immediately before making the next API call to prevent race condition
      setAuthToken(res.data.token);

      dispatch({ type: 'LOGIN_SUCCESS', payload: { authToken: res.data.token } });

      // Load user after successful login (token is now available for the request)
      const userRes = await apiClient.get('/auth/me');
      dispatch({ type: 'USER_LOADED', payload: userRes.data.data });
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 'Login failed';
      dispatch({
        type: 'LOGIN_FAIL',
        payload: errorMessage,
      });
      // Re-throw the error with full response data so components can access errorType
      throw err;
    }
  }, []);

  const register = useCallback(async (username: string, email: string, password: string) => {
    try {
      const res = await apiClient.post('/auth/register', { username, email, password });

      // Set token immediately before making the next API call
      setAuthToken(res.data.token);

      dispatch({ type: 'REGISTER_SUCCESS', payload: { authToken: res.data.token } });

      // Load user after successful registration (token is now available for the request)
      const userRes = await apiClient.get('/auth/me');
      dispatch({ type: 'USER_LOADED', payload: userRes.data.data });
    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.error || 'Registration failed';
      dispatch({
        type: 'REGISTER_FAIL',
        payload: errorMessage,
      });
      // Re-throw the error with full response data so components can access errorType
      throw err;
    }
  }, []);

  const updateProfile = useCallback(
    async (userData: { username?: string; email?: string; displayName?: string }) => {
      try {
        const res = await apiClient.put('/auth/profile', userData);
        dispatch({ type: 'PROFILE_UPDATE_SUCCESS', payload: res.data.data });
        return res.data.data;
      } catch (err: any) {
        console.error('Profile update error:', err);
        dispatch({
          type: 'PROFILE_UPDATE_FAIL',
          payload: err.response?.data?.error || 'Profile update failed',
        });
        throw err;
      }
    },
    []
  );

  const updatePassword = useCallback(
    async (passwords: { currentPassword: string; newPassword: string }) => {
      try {
        await apiClient.put('/auth/password', passwords);
        dispatch({ type: 'PASSWORD_UPDATE_SUCCESS' });
      } catch (err: any) {
        console.error('Password update error:', err);
        dispatch({
          type: 'PASSWORD_UPDATE_FAIL',
          payload: err.response?.data?.error || 'Password update failed',
        });
        throw err;
      }
    },
    []
  );

  const loginWithToken = useCallback(async (authToken: string) => {
    try {
      // Set the token and dispatch LOGIN_SUCCESS
      dispatch({ type: 'LOGIN_SUCCESS', payload: { authToken: authToken } });

      // Load user data
      const userRes = await apiClient.get('/auth/me');
      dispatch({ type: 'USER_LOADED', payload: userRes.data.data });
    } catch (err: any) {
      console.error('Login with token error:', err);
      dispatch({ type: 'AUTH_ERROR', payload: 'Authentication failed' });
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ERRORS' });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      authToken: state.authToken,
      isAuthenticated: state.isAuthenticated,
      loading: state.loading,
      user: state.user,
      error: state.error,
      login,
      register,
      loginWithToken,
      logout,
      updateProfile,
      updatePassword,
      clearErrors,
    }),
    [
      state.authToken,
      state.isAuthenticated,
      state.loading,
      state.user,
      state.error,
      login,
      register,
      loginWithToken,
      logout,
      updateProfile,
      updatePassword,
      clearErrors,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
